import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, type ReactNode, useEffect, useState, useSyncExternalStore } from 'react';
import { Navigate, useLocation } from 'react-router';
import { ApiError, errorMessage, get, post, setUnauthorizedHandler } from './api/client';
import type { User } from './api/types';
import { Field, Modal, Spinner } from './components/ui';
import { useT } from './i18n';
import { BRAND, SITE, SITE_LABELS } from './site';

export const ME_KEY = ['me'] as const;

// ------------------------------------------------------------------ sesión vencida
// Si la sesión se cierra mientras alguien trabaja (cambió la contraseña en otro lado, un
// administrador lo desactivó), se pide entrar de nuevo en un diálogo sobre la misma página:
// así no se pierde lo que se estaba editando.
let expired = false;
const listeners = new Set<() => void>();
function setExpired(value: boolean): void {
  if (expired === value) return;
  expired = value;
  for (const l of listeners) l();
}
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
const useExpired = () => useSyncExternalStore(subscribe, () => expired);

/** Solo rutas internas del dashboard ("/motos"), nunca "//otro-sitio". */
export const safeNext = (next: string | null) => (next && /^\/(?![/\\])/.test(next) ? next : '/');

/** Usuario de la sesión actual (null si no hay sesión). */
export function useMe() {
  const qc = useQueryClient();
  setUnauthorizedHandler(() => {
    if (qc.getQueryData(ME_KEY)) setExpired(true);
    else qc.setQueryData(ME_KEY, null);
  });
  return useQuery({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        return (await get<{ user: User }>('/admin/auth/me')).user;
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: Infinity,
    retry: false,
  });
}

/** Después de entrar (en la página de login o en el diálogo). */
export function useSignedIn() {
  const qc = useQueryClient();
  return (user: User) => {
    qc.setQueryData(ME_KEY, user);
    setExpired(false);
  };
}

export function useLogout() {
  const qc = useQueryClient();
  return async () => {
    await post('/admin/auth/logout').catch(() => undefined);
    setExpired(false);
    qc.clear();
    qc.setQueryData(ME_KEY, null);
  };
}

/** Pide sesión; si no hay, manda al login y vuelve a la misma página después. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const me = useMe();
  const location = useLocation();
  const isExpired = useExpired();
  useEffect(() => {
    if (me.data === null) setExpired(false);
  }, [me.data]);

  if (me.isPending) {
    return (
      <div className="center">
        <Spinner />
      </div>
    );
  }
  if (!me.data) {
    const next = location.pathname + location.search;
    return <Navigate to={`/entrar${next !== '/' ? `?next=${encodeURIComponent(next)}` : ''}`} replace />;
  }
  // La API respondió con la sesión de la otra marca: este dashboard no muestra nada de ella (solo
  // pasaría si CloudFront no le dijera a la API de qué dashboard viene la petición).
  if (me.data.site !== SITE) return <WrongBrand site={me.data.site} />;
  return (
    <>
      {children}
      {isExpired && <SessionExpiredDialog email={me.data.email} />}
    </>
  );
}

function WrongBrand({ site }: { site: string }) {
  const { t } = useT();
  const logout = useLogout();
  return (
    <div className="center">
      <div className="panel login">
        <img src="/logo.svg" alt={BRAND.name} className="logo" />
        <div className="notice error">
          {t('La API abrió una sesión del dashboard de {site} en el de {brand}. Revisa que su CloudFront le pase la marca a la API (X-Dashboard-Site).', {
            site: SITE_LABELS[site as keyof typeof SITE_LABELS] ?? site,
            brand: BRAND.name,
          })}
        </div>
        <button className="btn btn-block" onClick={() => void logout()}>
          {t('Cerrar sesión')}
        </button>
      </div>
    </div>
  );
}

function SessionExpiredDialog({ email }: { email: string }) {
  const { t } = useT();
  const qc = useQueryClient();
  const signedIn = useSignedIn();
  const logout = useLogout();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { user } = await post<{ user: User }>('/admin/auth/login', { email, password });
      signedIn(user);
      // Se refresca lo que se ve; lo que se estaba editando se conserva.
      void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== ME_KEY[0] });
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <Modal title={t('Tu sesión se cerró')} onClose={() => undefined} dismissible={false}>
      <form className="stack" onSubmit={submit}>
        <p className="muted" style={{ margin: 0 }}>
          {t('Vuelve a escribir tu contraseña para seguir. Lo que estabas editando sigue aquí: guárdalo después de entrar.')}
        </p>
        {error && <div className="notice error">{error}</div>}
        <Field label={t('Correo')}>{(id) => <input id={id} className="input" type="email" autoComplete="username" value={email} readOnly />}</Field>
        <Field label={t('Contraseña')}>
          {(id) => (
            <input id={id} className="input" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
          )}
        </Field>
        <div className="row between">
          <button type="button" className="btn btn-ghost" onClick={() => void logout()}>
            {t('Salir sin guardar')}
          </button>
          <button className="btn btn-primary" disabled={busy || !password}>
            {busy ? t('Entrando…') : t('Entrar')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
